// src/components/CompetitionLeaderboard.js
import { API_URL } from '../config/apiConfig';
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Dialog,
    TextField,
    Grid,
    IconButton,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    useMediaQuery,
    useTheme,
    FormControlLabel,
    Checkbox,
    DialogContentText,
    Alert
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { getCompetitionLeaderboard, submitCompetitionResult, addCompetitorByAdmin } from '../api/competitionApi';
import ParticipantDetailDialog from './ParticipantDetailDialog';
import axios from 'axios';


export default function CompetitionLeaderboard({ competition, isAdminView }) {
    const [leaderboard, setLeaderboard] = useState([]);
    const [selectedWorkout, setSelectedWorkout] = useState('overall');
    const [selectedCategory, setSelectedCategory] = useState(competition?.categories?.[0] || 'all');

    const [loading, setLoading] = useState(true);
    const [addingScoresFor, setAddingScoresFor] = useState(null);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [openParticipantDialog, setOpenParticipantDialog] = useState(false);
    const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
    const [openScoreDialog, setOpenScoreDialog] = useState(false);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Form state for adding scores
    const [scoreForm, setScoreForm] = useState({
        result: '',
        videoUrl: '',
        timeCap: false,
        reps: '',
        tiebreak: ''
    });

    // Registration form state
    const [registerForm, setRegisterForm] = useState({
        userId: null,
        teamName: '',
        category: '',
        teamMembers: []
    });
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [registerLoading, setRegisterLoading] = useState(false);

    useEffect(() => {
        loadLeaderboard();
    }, [competition.id]);

    const loadLeaderboard = async () => {
        try {
            setLoading(true);
            const data = await getCompetitionLeaderboard(competition.id);

            setLeaderboard(data.leaderboard);

            // ✅ Uuenda selectedParticipant kui dialog on avatud
            if (selectedParticipant) {
                const updatedParticipant = data.leaderboard.find(
                    p => p.registrationId === selectedParticipant.registrationId
                );
                if (updatedParticipant) {
                    setSelectedParticipant(updatedParticipant);
                }
            }
        } catch (error) {
            console.error('Error loading leaderboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleWorkoutChange = (event) => {
        setSelectedWorkout(event.target.value);
    };

    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value);
    };

    const handleAddScores = (participant) => {
        setAddingScoresFor(participant.registrationId);
        const existingScore = participant.scores[selectedWorkout];
        setScoreForm({ 
            result: existingScore?.result && !existingScore?.timeCap ? existingScore.result : '', 
            videoUrl: existingScore?.videoUrl || '', 
            timeCap: existingScore?.timeCap || false, 
            reps: existingScore?.reps?.toString() || '',
            tiebreak: existingScore?.tiebreak || ''
        });
        setOpenScoreDialog(true);
    };

    const handleCancelAddScores = () => {
        setAddingScoresFor(null);
        setScoreForm({ result: '', videoUrl: '', timeCap: false, reps: '', tiebreak: '' });
        setOpenScoreDialog(false);
    };

    const handleSubmitScore = async () => {
        const participant = leaderboard.find(p => p.registrationId === addingScoresFor);
        if (!participant) return;

        const workout = competition.workouts.find(w => w.id === parseInt(selectedWorkout));
        if (!workout) return;

        // ✅ Validate based on TC
        if (workout.resultType === 'TIME' && scoreForm.timeCap) {
            if (!scoreForm.reps || isNaN(scoreForm.reps)) {
                setAlertMessage('Please enter valid reps number for TC result');
                setAlertOpen(true);
                return;
            }
        } else {
            if (!scoreForm.result) {
                setAlertMessage('Please enter a result');
                setAlertOpen(true);
                return;
            }

            if (!validateResultFormat(scoreForm.result, workout.resultType)) {
                setAlertMessage(getFormatHint(workout.resultType));
                setAlertOpen(true);
                return;
            }
        }

        // Validate tiebreak if workout has tiebreak enabled
        if (workout.tiebreak && workout.tiebreakType) {
            if (scoreForm.tiebreak && !validateTiebreakFormat(scoreForm.tiebreak, workout.tiebreakType)) {
                if (workout.tiebreakType === 'TIME') {
                    setAlertMessage('Tiebreak time must be in mm:ss format (e.g., 12:34)');
                } else {
                    setAlertMessage('Tiebreak must be a valid number (e.g., 123.45 or 123,45)');
                }
                setAlertOpen(true);
                return;
            }
        }

        try {
            setErrorMessage('');
            const userId = JSON.parse(localStorage.getItem('user'))?.id;
            await submitCompetitionResult(competition.id, selectedWorkout, {
                registrationId: participant.registrationId,
                result: scoreForm.timeCap ? `TC-${scoreForm.reps}` : scoreForm.result,
                videoUrl: scoreForm.videoUrl || null,
                submittedBy: userId,
                timeCap: scoreForm.timeCap,
                reps: scoreForm.timeCap ? parseInt(scoreForm.reps) : null,
                tiebreak: workout.tiebreak && scoreForm.tiebreak ? scoreForm.tiebreak : null
            });

            setAddingScoresFor(null);
            setScoreForm({ result: '', videoUrl: '', timeCap: false, reps: '', tiebreak: '' });
            setOpenScoreDialog(false);
            loadLeaderboard();
        } catch (error) {
            console.error('Error submitting score:', error);
            setErrorMessage('Failed to submit score');
        }
    };

    const validateResultFormat = (result, resultType) => {
        if (resultType === 'TIME') {
            return /^\d{2}:\d{2}:\d{2}$/.test(result);
        } else if (resultType === 'REPS') {
            return /^\d+$/.test(result);
        } else if (resultType === 'WEIGHT') {
            return /^\d+(\.\d+)?$/.test(result);
        }
        return false;
    };

    const validateTiebreakFormat = (tiebreak, tiebreakType) => {
        if (!tiebreak) return true; // Optional field
        if (tiebreakType === 'TIME') {
            // mm:ss format
            return /^\d{1,2}:\d{2}$/.test(tiebreak);
        } else if (tiebreakType === 'NUMBER') {
            // Number with comma or dot
            return /^\d+([.,]\d+)?$/.test(tiebreak);
        }
        return false;
    };

    const getFormatHint = (resultType) => {
        if (resultType === 'TIME') {
            return 'Time (hh:mm:ss)';
        } else if (resultType === 'REPS') {
            return 'Reps';
        } else if (resultType === 'WEIGHT') {
            return 'Weight (kg)';
        }
        return '';
    };

    const handleParticipantClick = (participant) => {
        setSelectedParticipant(participant);
        setOpenParticipantDialog(true);
    };

    const searchUsers = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setUserSearchResults([]);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/search-users`, {
                params: { q: searchTerm },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setUserSearchResults(response.data);
        } catch (error) {
            console.error('Error searching users:', error);
        }
    };

    const handleOpenRegisterDialog = () => {
        setRegisterForm({
            userId: null,
            teamName: '',
            category: '',
            teamMembers: competition.isTeamCompetition ? [] : []
        });
        setOpenRegisterDialog(true);
    };

    const handleRegisterSubmit = async () => {
        if (!registerForm.category) {
            setAlertMessage('Please select a category');
            setAlertOpen(true);
            return;
        }

        if (competition.isTeamCompetition) {
            if (!registerForm.teamName) {
                setAlertMessage('Please enter a team name');
                setAlertOpen(true);
                return;
            }
            if (registerForm.teamMembers.filter(m => m).length !== competition.teamSize) {
                setAlertMessage(`Please add exactly ${competition.teamSize} team members`);
                setAlertOpen(true);
                return;
            }
        } else {
            if (!registerForm.userId) {
                setAlertMessage('Please select a participant');
                setAlertOpen(true);
                return;
            }
        }

        try {
            setRegisterLoading(true);
            setErrorMessage('');
            await addCompetitorByAdmin(competition.id, {
                userId: competition.isTeamCompetition ? null : registerForm.userId,
                teamName: competition.isTeamCompetition ? registerForm.teamName : null,
                category: registerForm.category,
                teamMembers: competition.isTeamCompetition ? registerForm.teamMembers.filter(m => m) : null
            });
            setOpenRegisterDialog(false);
            loadLeaderboard();
        } catch (error) {
            console.error('Error adding competitor:', error);
            setErrorMessage(error.response?.data?.error || 'Failed to add competitor');
        } finally {
            setRegisterLoading(false);
        }
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        setAlertMessage('');
    };

    const handleTeamMemberChange = (index, value) => {
        const newTeamMembers = [...registerForm.teamMembers];
        newTeamMembers[index] = value?.id || null;
        setRegisterForm(prev => ({
            ...prev,
            teamMembers: newTeamMembers
        }));
    };

    const getDisplayedColumns = () => {
        if (selectedWorkout === 'overall') {
            return [
                { label: 'Rank', width: '10%' },
                { label: competition.isTeamCompetition ? 'Team Name' : 'Participant', width: '50%' },
                { label: 'Category', width: '20%' },
                { label: 'Total Points', width: '20%' }
            ];
        } else {
            return [
                { label: 'Rank', width: '10%' },
                { label: competition.isTeamCompetition ? 'Team Name' : 'Participant', width: '40%' },
                { label: 'Result', width: '25%' },
                { label: 'Workout Points (Overall)', width: '25%' }
            ];
        }
    };

    const getSortedLeaderboard = () => {
        const filteredLeaderboard = leaderboard.filter(p => p.category === selectedCategory);

        if (selectedWorkout === 'overall') {
            return filteredLeaderboard.sort((a, b) => {
                const pointsDifference = b.totalPoints - a.totalPoints;
                if (pointsDifference !== 0) {
                    return pointsDifference;
                }

                const placeCount = competition.workouts?.length || 0;

                for (let placeIndex = 0; placeIndex < placeCount; placeIndex++) {
                    const getPlacementCount = (participant) => {
                        if (Array.isArray(participant.placementCounts)) {
                            return participant.placementCounts[placeIndex] || 0;
                        }

                        return Object.values(participant.scores).filter(
                            score => score.place === placeIndex + 1
                        ).length;
                    };

                    const placementDifference =
                        getPlacementCount(b) - getPlacementCount(a);

                    if (placementDifference !== 0) {
                        return placementDifference;
                    }
                }

                return a.registrationId - b.registrationId;
            });
        } else {
            return filteredLeaderboard.sort((a, b) => {
                const aScore = a.scores[selectedWorkout];
                const bScore = b.scores[selectedWorkout];

                if (!aScore && !bScore) return 0;
                if (!aScore) return 1;
                if (!bScore) return -1;

                if (competition.scoringSystem.higherIsBetter) {
                    return bScore.points - aScore.points;
                } else {
                    return aScore.points - bScore.points;
                }
            });
        }
    };

    const sortedLeaderboard = getSortedLeaderboard();

    return (
        <Box>
            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                    {errorMessage}
                </Alert>
            )}

            {/* Mobile Layout */}
            <Box sx={{
                display: { xs: 'flex', sm: 'none' },
                flexDirection: 'column',
                gap: 2,
                mb: 3
            }}>
                <FormControl fullWidth>
                    <InputLabel>Select Category</InputLabel>
                    <Select
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        label="Select Category"
                    >
                        {competition.categories && competition.categories.map((category) => (
                            <MenuItem key={category} value={category}>
                                {category}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel>Select Workout</InputLabel>
                    <Select
                        value={selectedWorkout}
                        onChange={handleWorkoutChange}
                        label="Select Workout"
                    >
                        <MenuItem value="overall">Overall Standings</MenuItem>
                        {competition.workouts && competition.workouts.map((workout) => (
                            <MenuItem key={workout.id} value={workout.id}>
                                {workout.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {isAdminView && (
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenRegisterDialog}
                        fullWidth
                    >
                        Add Competitor
                    </Button>
                )}
            </Box>

            {/* Desktop Layout */}
            <Box sx={{
                display: { xs: 'none', sm: 'flex' },
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
            }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Select Category</InputLabel>
                        <Select
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            label="Select Category"
                        >
                            {competition.categories && competition.categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                    {category}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 250 }}>
                        <InputLabel>Select Workout</InputLabel>
                        <Select
                            value={selectedWorkout}
                            onChange={handleWorkoutChange}
                            label="Select Workout"
                        >
                            <MenuItem value="overall">Overall Standings</MenuItem>
                            {competition.workouts && competition.workouts.map((workout) => (
                                <MenuItem key={workout.id} value={workout.id}>
                                    {workout.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {isAdminView && (
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenRegisterDialog}
                    >
                        Add Competitor
                    </Button>
                )}
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {getDisplayedColumns().map((col, idx) => (
                                <TableCell key={idx} width={col.width}>
                                    <strong>{col.label}</strong>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedLeaderboard.map((participant, index) => (
                            <TableRow
                                key={participant.registrationId}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleParticipantClick(participant)}
                            >
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                    {participant.name || `Participant ${participant.userId}`}
                                </TableCell>

                                {selectedWorkout === 'overall' ? (
                                    <>
                                        <TableCell>{participant.category}</TableCell>
                                        <TableCell>
                                            <strong>{participant.totalPoints}</strong>
                                        </TableCell>
                                    </>
                                ) : (
                                    <>
                                        <TableCell>
                                            {participant.scores[selectedWorkout]?.timeCap
                                                ? `TC - ${participant.scores[selectedWorkout]?.reps} reps`
                                                : (participant.scores[selectedWorkout]?.result || '-')}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box>
                                                    <strong>
                                                        {participant.scores[selectedWorkout]?.points || 0}
                                                    </strong>
                                                    {' '}
                                                    <span style={{ color: 'gray' }}>
                                                        ({participant.totalPoints})
                                                    </span>
                                                </Box>
                                                {isAdminView && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleAddScores(participant);
                                                        }}
                                                        title="Add/Edit Score"
                                                    >
                                                        <Add fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        ))}

                        {sortedLeaderboard.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography color="textSecondary">
                                        No participants registered yet
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Register Participant Dialog */}
            <Dialog open={openRegisterDialog} onClose={() => setOpenRegisterDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Add Competitor to {competition?.name}
                        </Typography>
                        <IconButton onClick={() => setOpenRegisterDialog(false)} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    <Grid container spacing={3}>
                        {competition?.isTeamCompetition ? (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        Add a team with {competition.teamSize} members.
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Team Name *"
                                        value={registerForm.teamName}
                                        onChange={(e) => setRegisterForm(prev => ({ ...prev, teamName: e.target.value }))}
                                        placeholder="Enter a unique team name"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Team Members
                                    </Typography>
                                </Grid>

                                {Array.from({ length: competition.teamSize }, (_, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Autocomplete
                                            options={userSearchResults}
                                            getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                                            onChange={(e, value) => handleTeamMemberChange(index, value)}
                                            onInputChange={(e, value) => searchUsers(value)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={`Member ${index + 1} *`}
                                                    placeholder="Search for a member"
                                                />
                                            )}
                                        />
                                    </Grid>
                                ))}
                            </>
                        ) : (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        Add an individual competitor by searching for their name.
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Autocomplete
                                        options={userSearchResults}
                                        getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                                        onChange={(e, value) => setRegisterForm(prev => ({ ...prev, userId: value?.id || null }))}
                                        onInputChange={(e, value) => searchUsers(value)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Participant *"
                                                placeholder="Search for a participant"
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={registerForm.category}
                                    onChange={(e) => setRegisterForm(prev => ({ ...prev, category: e.target.value }))}
                                    label="Category"
                                >
                                    {competition?.categories.map((category) => (
                                        <MenuItem key={category} value={category}>
                                            {category}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenRegisterDialog(false)} disabled={registerLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRegisterSubmit}
                        disabled={registerLoading}
                    >
                        {registerLoading ? 'Adding...' : 'Add Competitor'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alert Dialog */}
            <Dialog
                open={alertOpen}
                onClose={handleAlertClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="alert-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Warning
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="alert-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleAlertClose}
                        color="primary"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Score Dialog */}
            <Dialog 
                open={openScoreDialog} 
                onClose={handleCancelAddScores} 
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            {addingScoresFor ? 'Add/Edit Score' : 'Add Score'}
                        </Typography>
                        <IconButton onClick={handleCancelAddScores} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                            {errorMessage}
                        </Alert>
                    )}

                    <Grid container spacing={3}>
                        {(() => {
                            const workout = competition.workouts.find(w => w.id === parseInt(selectedWorkout));
                            if (!workout) return null;

                            return (
                                <>
                                    {/* TC Checkbox for TIME workouts */}
                                    {workout.resultType === 'TIME' && (
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={scoreForm.timeCap}
                                                        onChange={(e) => setScoreForm({
                                                            ...scoreForm,
                                                            timeCap: e.target.checked,
                                                            result: '',
                                                            reps: ''
                                                        })}
                                                    />
                                                }
                                                label="Time Cap (TC)"
                                            />
                                        </Grid>
                                    )}

                                    {/* Result input based on TC */}
                                    <Grid item xs={12}>
                                        {scoreForm.timeCap ? (
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Reps *"
                                                value={scoreForm.reps}
                                                onChange={(e) => setScoreForm({ ...scoreForm, reps: e.target.value })}
                                                inputProps={{ min: 0 }}
                                                placeholder="Enter number of reps"
                                            />
                                        ) : (
                                            <TextField
                                                fullWidth
                                                label="Result *"
                                                value={scoreForm.result}
                                                onChange={(e) => setScoreForm({ ...scoreForm, result: e.target.value })}
                                                placeholder={getFormatHint(workout.resultType)}
                                                helperText={getFormatHint(workout.resultType)}
                                            />
                                        )}
                                    </Grid>

                                    {/* Tiebreak field */}
                                    {workout.tiebreak && workout.tiebreakType && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={`Tiebreak (${workout.tiebreakType === 'TIME' ? 'mm:ss' : 'Number'})`}
                                                value={scoreForm.tiebreak}
                                                onChange={(e) => setScoreForm({ ...scoreForm, tiebreak: e.target.value })}
                                                placeholder={
                                                    workout.tiebreakType === 'TIME' 
                                                        ? 'e.g., 12:34' 
                                                        : 'e.g., 123.45 or 123,45'
                                                }
                                                helperText={
                                                    workout.tiebreakType === 'TIME'
                                                        ? 'Enter time in mm:ss format (e.g., 12:34)'
                                                        : 'Enter a number with comma or dot (e.g., 123.45 or 123,45)'
                                                }
                                            />
                                        </Grid>
                                    )}

                                    {/* Video URL */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Video URL (optional)"
                                            value={scoreForm.videoUrl}
                                            onChange={(e) => setScoreForm({ ...scoreForm, videoUrl: e.target.value })}
                                            placeholder="https://..."
                                        />
                                    </Grid>
                                </>
                            );
                        })()}
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCancelAddScores}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmitScore}>
                        Save Score
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Participant Detail Dialog */}
            <ParticipantDetailDialog
                open={openParticipantDialog}
                onClose={() => setOpenParticipantDialog(false)}
                participant={selectedParticipant}
                competition={competition}
                leaderboard={leaderboard}
                sx={{px: { xs: 0, sm: 3 }}}
                canViewVideos={isAdminView}
                onApprovalChange={loadLeaderboard}
                isAdminView={isAdminView}
            />
        </Box>
    );
}